/**
 * Resolves the fallback DOM anchor for InPlace ad insertion.
 *
 * Returns an existing DOM element — never creates or inserts anything.
 * The returned element is passed to insertWrapper, which decides
 * inside-vs-after based on the element type.
 *
 * WHY THE PUSH SITE COMES FIRST: the bootstrap <script> is only a valid
 * anchor when the publisher inlined tag.js at the slot (the classic
 * snippet). Loader/harness integrations instead append tag.js to
 * `document.head` and inject the `__neuronic.push(...)` snippet into the
 * slot element — anchoring on the bootstrap script then renders the ad
 * INTO THE HEAD: connected, zero-area (UA hides head content), creative
 * iframe loads and fires its pixels, and the slot stays visually empty.
 * Observed live on a harness publisher. The push snippet is the one
 * element that is always AT the slot, so it wins whenever we can find it.
 *
 * Precedence:
 *   1. The push site (any frame)         → the <script> that pushed this
 *                                          placement (see findPushSite)
 *   3. On-page, bootstrap NOT in <head>  → the bootstrap <script> element
 *   4. Fallback                          → document.body
 *
 * @param {object} renderEnv               - from getRenderEnv()
 * @param {HTMLScriptElement | null} bootstrapScript
 * @param {Element | null} pushSite        - from findPushSite()
 * @returns {Element}
 */
export function resolveDefaultTarget(renderEnv, bootstrapScript, pushSite = null) {
  if (pushSite)
    return pushSite;

  if (!renderEnv.inIframe && isRenderableAnchor(bootstrapScript))
    return bootstrapScript;

  return document.body;
}

/**
 * Locates the <script> element that pushed this placement — the one
 * element guaranteed to sit AT the publisher's slot.
 *
 *   - Post-load pushes: the bootstrap's live push() override captures
 *     `document.currentScript` synchronously during the pusher's
 *     execution; when that script is still in the DOM it IS the site.
 *   - Pre-load queued pushes ran before tag.js executed, so currentScript
 *     is unrecoverable — find the inline snippet instead: the only inline
 *     script whose text contains BOTH this placement id and the queue
 *     namespace is the push snippet itself.
 *
 * Returns null when no renderable site is found (e.g. the snippet was
 * itself removed) — the caller falls back down the anchor precedence.
 *
 * @param {string} placementId
 * @param {HTMLScriptElement | null} pushScript - captured at push time
 * @returns {Element | null}
 */
export function findPushSite(placementId, pushScript) {
  if (isRenderableAnchor(pushScript))
    return pushScript;

  for (const script of document.scripts) {
    if (script.src)
      continue;

    const text = script.textContent;

    if (text.includes(placementId) && text.includes(__NAMESPACE__) && isRenderableAnchor(script))
      return script;
  }

  return null;
}

/**
 * An anchor is renderable when inserting a sibling after it produces a
 * VISIBLE element: it exists, is connected, and is not inside <head>
 * (the UA stylesheet hides head content, so a head-anchored ad is
 * connected but zero-area — the exact live failure this guards).
 */
function isRenderableAnchor(el) {
  return !!el && el.isConnected && !el.closest('head');
}
